"use client";

import type React from "react";
import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useAppTheme } from "../../ThemeContext.tsx";
import type { KnowledgeBaseText } from "../../services/knowledgeBaseService.ts";

interface AddTextModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (text: KnowledgeBaseText) => void;
}

export const AddTextModal: React.FC<AddTextModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleSubmit = () => {
    if (!title.trim()) {
      toast({
        title: "Title is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!content.trim()) {
      toast({
        title: "Content is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    onSave({ title, text: content });
    setTitle("");
    setContent("");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={isDark ? "gray.800" : "white"}>
        <ModalHeader color={isDark ? "white" : "gray.800"}>
          Add Text
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                File Name
              </FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter file name"
                bg={isDark ? "gray.700" : "white"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                color={isDark ? "white" : "gray.800"}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Text Content
              </FormLabel>
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter text content"
                bg={isDark ? "gray.700" : "white"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                color={isDark ? "white" : "gray.800"}
                minH="200px"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            color={isDark ? "gray.300" : "gray.700"}
            _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSubmit}
            _hover={{ bg: isDark ? "gray.600" : "gray.500" }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
